import { InventaireResponse } from '@repo/types';
import { AESTHETIC_INDEX_TAB, DataIndexConst, HEALTH_INDEX_TAB, LOCATION_INDEX_TAB } from '../../../../../lib/inventory/constant/beva.const';
import { FormData } from '../../../shared/types';

// Define the structure of the server response with snake_case field names
export interface InventaireServerResponse {
  data: {
    id: number;
    created_at: string;
    updated_at: string;
    is_finished: boolean;
    type: string;
    variety_grade: number;
    health_index: number;
    aesthetic_index: number;
    location_index: number;
    address: string;
    ville: string;
    aesthetic_column: number;
    health_column: number;
    user_id: number;
    arbre_id: number;
    epaysage_id?: number | null;
    epaysage?: any;
    arbre: {
      id: number;
      diametre: number;
      created_at: string;
      updated_at: string;
      coord: {
        lat: number;
        lng: number;
      };
      code_site: string;
      num_sujet: string;
      critere: string[];
      implantation: string;
      domaine: string;
      nuisance: string[];
      proximite: string[];
      taux_freq: string;
      type_passage: string[];
      accessibilite: string;
      abattage: string;
      travaux_collet?: string | null;
      travaux_tronc?: string | null;
      travaux_houppier?: string | null;
      date_travaux: string;
      date_pro_visite: string;
      com_pro_visite?: string | null;
      caract_pied: string[];
      caract_tronc: string;
      hauteur: number;
      port_arbre: string;
      stade_dev: string;
      etat_san_collet: string[];
      etat_san_tronc: string[];
      etat_san_houppier: string[];
      address: string;
      com_access?: string | null;
      dict?: any;
      risque: Array<{ [key: string]: string }>;
      proximite_other?: string | null;
      accessibilite_other?: string | null;
      caract_pied_other?: string | null;
      caract_tronc_multiples?: number | null;
      etat_san_collet_cavite?: string | null;
      etat_san_collet_champignons: string[];
      etat_san_tronc_cavite?: string | null;
      etat_san_tronc_corps_etranger?: string | null;
      etat_san_tronc_champignons: string[];
      etat_san_tronc_nuisibles: string[];
      etat_san_houppier_champignons: string[];
      etat_san_houppier_nuisibles: string[];
      risque_general: string[];
      travaux_commentaire?: string | null;
      critere_other?: string | null;
      risque_general_other?: string | null;
      type_passage_other: string;
      type_intervention: string;
      etat_san_general: string[];
      user_edited_date_travaux: string;
      travaux_tronc_other?: string | null;
      travaux_collet_other?: string | null;
      travaux_houppier_other?: string | null;
      proximite_with_dict: any[];
      ville: string;
      pays: string;
      travaux_tronc_protection?: string | null;
      etat_san_tronc_nuisibles_autres?: string | null;
      etat_san_houppier_nuisibles_autres?: string | null;
      travaux_collet_multiple: string[];
      travaux_tronc_multiple: string[];
      travaux_houppier_multiple: string[];
      status_travaux: boolean;
      etat_san_collet_champignons_autres?: string | null;
      etat_san_tronc_champignons_autres?: string | null;
      etat_san_houppier_champignons_autres?: string | null;
      etat_san_collet_other?: string | null;
      etat_san_tronc_other?: string | null;
      etat_san_houppier_other?: string | null;
      etat_san_general_other?: string | null;
      espece_id: number;
      espece: {
        id: number;
        name: string;
        genre: string;
        nomFr: string;
        cultivar?: string | null;
      };
      imgUrl: {
        img1?: string | null;
        img2?: string | null;
        img3?: string | null;
      };
    };
    user: {
      username: string;
      email: string;
      id: number;
    };
  };
}

/**
 * Maps server response data to form data structure
 * Based on the field mappings from fields-tree.json and form-config.ts
 */
export function mapServerDataToFormData(serverData: InventaireResponse): FormData {
  if (!serverData.data) {
    throw new Error('Server data is required');
  }
  const { arbre } = serverData.data;
  const formData: FormData = {};

  // ========================================
  // STEP 1: Caractéristiques du sujet
  // ========================================

  // Diameter and height
  if (arbre.diametre !== undefined) {
    formData['diametre'] = arbre.diametre;
  }
  if (arbre.age_estime !== undefined) {
    formData['age_estime'] = arbre.age_estime;
  }
  if (arbre.hauteur !== undefined) {
    formData['hauteur'] = arbre.hauteur;
  }
  if (arbre.num_sujet) {
    formData['num_sujet'] = arbre.num_sujet;
  }
  if (arbre.code_site) {
    formData['code_site'] = arbre.code_site;
  }

  // Au pied de l'arbre - maps to caract_pied (multiselect)
  if (arbre.caract_pied) {
    formData['au-pied-de-l-arbre'] = arbre.caract_pied;
  }
  if (arbre.caract_pied_other) {
    formData['au-pied-de-l-arbre_other'] = arbre.caract_pied_other;
  }

  // Tronc characteristics - maps to caract_tronc
  if (arbre.caract_tronc) {
    formData['caracteristiques-du-sujet-tronc'] = arbre.caract_tronc;
  }
  if (arbre.caract_tronc_multiples) {
    formData['caracteristiques-du-sujet-tronc_tronc-multiples'] = arbre.caract_tronc_multiples;
  }

  // Port de l'arbre - maps to port_arbre
  if (arbre.port_arbre) {
    formData['port-de-larbre'] = arbre.port_arbre;
  }

  // Stade de développement - maps to stade_dev
  if (arbre.stade_dev) {
    formData['stade-de-developpement'] = arbre.stade_dev;
  }

  // ========================================
  // STEP 2: Aspect remarquable
  // ========================================

  // Critères remarquables - maps to critere
  if (arbre.critere) {
    formData['critere-remarquable'] = arbre.critere;
  }
  if (arbre.critere_other) {
    formData['critere-remarquable_autre-precisez'] = arbre.critere_other;
  }

  // ========================================
  // STEP 3: État sanitaire
  // ========================================

  // État sanitaire système racinaire et collet - maps to etat_san_collet
  if (arbre.etat_san_collet) {
    formData['etat-sanitaire-systme-racinaire-collet'] = arbre.etat_san_collet;
  }
  if (arbre.etat_san_collet_cavite) {
    formData['etat-sanitaire-systme-racinaire-collet_cavit-nombre'] = arbre.etat_san_collet_cavite;
  }
  if (arbre.etat_san_collet_champignons) {
    formData['etat-sanitaire-systme-racinaire-collet_champignons-lignivores'] = arbre.etat_san_collet_champignons;
  }
  if (arbre.etat_san_collet_champignons_autres) {
    formData['etat-sanitaire-systme-racinaire-collet_champignons-lignivores_autres'] = arbre.etat_san_collet_champignons_autres;
  }
  if (arbre.etat_san_collet_other) {
    formData['etat-sanitaire-systme-racinaire-collet_autre-precisez'] = arbre.etat_san_collet_other;
  }

  // État sanitaire tronc - maps to etat_san_tronc
  if (arbre.etat_san_tronc) {
    formData['etat-sanitaire-tronc'] = arbre.etat_san_tronc;
  }
  if (arbre.etat_san_tronc_cavite) {
    formData['etat-sanitaire-tronc_cavit-nombre'] = arbre.etat_san_tronc_cavite;
  }
  if (arbre.etat_san_tronc_corps_etranger) {
    formData['etat_san_tronc_corps_etranger'] = arbre.etat_san_tronc_corps_etranger;
  }
  if (arbre.etat_san_tronc_champignons) {
    formData['etat-sanitaire-tronc_champignons-lignivores'] = arbre.etat_san_tronc_champignons;
  }
  if (arbre.etat_san_tronc_champignons_autres) {
    formData['etat-sanitaire-tronc_champignons-lignivores_autres'] = arbre.etat_san_tronc_champignons_autres;
  }
  if (arbre.etat_san_tronc_nuisibles) {
    formData['etat-sanitaire-tronc_parasite'] = arbre.etat_san_tronc_nuisibles;
  }
  if (arbre.etat_san_tronc_nuisibles_autres) {
    formData['etat-sanitaire-tronc_parasite-other'] = arbre.etat_san_tronc_nuisibles_autres;
  }
  if (arbre.etat_san_tronc_other) {
    formData['etat-sanitaire-tronc_other'] = arbre.etat_san_tronc_other;
  }

  // État sanitaire houppier - maps to etat_san_houppier
  if (arbre.etat_san_houppier) {
    formData['etat-sanitaire-houppier'] = arbre.etat_san_houppier;
  }
  if (arbre.etat_san_houppier_champignons) {
    formData['etat-sanitaire-houppier_champignons-lignivores'] = arbre.etat_san_houppier_champignons;
  }
  if (arbre.etat_san_houppier_champignons_autres) {
    formData['etat-sanitaire-houppier_champignons-lignivores_autres'] = arbre.etat_san_houppier_champignons_autres;
  }
  if (arbre.etat_san_houppier_nuisibles) {
    formData['etat-sanitaire-houppier_parasite'] = arbre.etat_san_houppier_nuisibles;
  }
  if (arbre.etat_san_houppier_nuisibles_autres) {
    formData['etat-sanitaire-houppier_parasite-other'] = arbre.etat_san_houppier_nuisibles_autres;
  }
  if (arbre.etat_san_houppier_other) {
    formData['etat-sanitaire-houppier_other'] = arbre.etat_san_houppier_other;
  }

  // État sanitaire général - maps to etat_san_general
  if (arbre.etat_san_general && arbre.etat_san_general.length > 0) {
    formData['etat-sanitaire-general'] = arbre.etat_san_general; // Radio field, take first value
  }
  if (arbre.etat_san_general_other) {
    formData['etat-sanitaire-general_other'] = arbre.etat_san_general_other;
  }

  if (arbre.proximite_with_dict && arbre.proximite_with_dict.length > 0) {
    formData['dict'] = arbre.proximite_with_dict;
  }

  // ========================================
  // STEP 4: Risque de rupture
  // ========================================

  // Transform risque array to table format expected by the form
  if (arbre.risque && Array.isArray(arbre.risque)) {
    const risqueTable: { [key: string]: string } = {};
    arbre.risque.forEach(risk => {
      Object.entries(risk).forEach(([part, level]) => {
        //@ts-ignore
        risqueTable[part] = level;
      });
    });
    formData['risque-de-rupture'] = risqueTable;
  }

  // Risque général - maps to risque_general
  if (arbre.risque_general && arbre.risque_general.length > 0) {
    formData['risque-de-rupture-generale'] = arbre.risque_general; // Radio field
  }
  if (arbre.risque_general_other) {
    formData['risque-de-rupture-generale_other'] = arbre.risque_general_other;
  }

  // ========================================
  // STEP 5: Environnement
  // ========================================

  // Implantation - maps to implantation
  if (arbre.implantation) {
    formData['implantation'] = arbre.implantation;
  }

  // Domaine - maps to domaine
  if (arbre.domaine) {
    formData['domaine'] = arbre.domaine;
  }

  // Nuisances constatées - maps to nuisance
  if (arbre.nuisance) {
    formData['nuisances-constatees'] = arbre.nuisance;
  }

  // Proximité directe - maps to proximite
  if (arbre.proximite) {
    formData['proximite-directe'] = arbre.proximite;
  }
  if (arbre.proximite_other) {
    formData['proximite-directe_other'] = arbre.proximite_other;
  }

  // DICT - maps to dict
  if (arbre.proximite_with_dict) {
    formData['dict'] = arbre.proximite_with_dict;
  }

  // Taux de fréquentation - maps to taux_freq
  if (arbre.taux_freq) {
    formData['taux-de-frquentation'] = arbre.taux_freq;
  }

  // Type de passage - maps to type_passage
  if (arbre.type_passage) {
    formData['type-de-passage'] = arbre.type_passage;
  }
  if (arbre.type_passage_other) {
    formData['type-de-passage_other'] = arbre.type_passage_other;
  }

  // Accessibilité - maps to accessibilite
  if (arbre.accessibilite) {
    formData['accessibilite'] = arbre.accessibilite;
  }
  if (arbre.accessibilite_other) {
    formData['accessibilite_other'] = arbre.accessibilite_other;
  }

  // ========================================
  // STEP 6: Préconisation de travaux
  // ========================================

  // Date travaux - maps to date_travaux
  if (arbre.date_travaux) {
    formData['preco-travaux-date-travaux'] = arbre.date_travaux;
  }
  if (arbre.date_pro_visite) {
    formData['preco-travaux-prochaine-visite'] = arbre.date_pro_visite;
  }


  // Work recommendations - map various travaux fields
  if (arbre.abattage) {
    formData['preco-travaux-abattage'] = arbre.abattage;
  }


  // Multiple work types
  if (arbre.travaux_collet_multiple) {
    formData['preco-travaux-collet'] = arbre.travaux_collet_multiple;
  }

  if (arbre.travaux_tronc_multiple) {
    formData['preco-travaux-tronc'] = arbre.travaux_tronc_multiple;
  }

  if (arbre.travaux_houppier_multiple) {
    formData['preco-travaux-houppier'] = arbre.travaux_houppier_multiple;
  }


  // Work comments and other fields
  if (arbre.travaux_collet_other) {
    formData['preco-travaux-collet_other'] = arbre.travaux_collet_other;
  }
  if (arbre.travaux_tronc_other) {
    formData['preco-travaux-tronc_other'] = arbre.travaux_tronc_other;
  }
  if (arbre.travaux_houppier_other) {
    formData['preco-travaux-houppier_other'] = arbre.travaux_houppier_other;
  }
  if (arbre.travaux_tronc_protection) {
    formData['preco-travaux-tronc_protection'] = arbre.travaux_tronc_protection;
  }
  // Work comments
  if (arbre.travaux_commentaire) {
    formData['preco-travaux-com'] = arbre.travaux_commentaire;
  }

  // ========================================
  // STEP 7: Estimation de la valeur (BEVA)
  // ========================================

  // Get BEVA data from serverData.data (not arbre)
  const { health_index, aesthetic_index, location_index, variety_grade, health_column, aesthetic_column } = serverData.data;

  // Map BEVA fields directly to form data with new field names
  if (variety_grade !== undefined && variety_grade !== null) {
    formData['variety_grade'] = variety_grade;
  }

  if (health_index !== undefined && health_index !== null) {
    formData['health_index'] = health_index;
  }

  if (aesthetic_index !== undefined && aesthetic_index !== null) {
    formData['aesthetic_index'] = aesthetic_index;
  }

  if (location_index !== undefined && location_index !== null) {
    formData['location_index'] = location_index;
  }

  if (health_column !== undefined && health_column !== null) {
    formData['health_column'] = health_column;
  }

  if (aesthetic_column !== undefined && aesthetic_column !== null) {
    formData['aesthetic_column'] = aesthetic_column;
  }

  // Keep backward compatibility - map to old field names if needed for legacy components
  if (health_column !== undefined && health_column !== null) {
    formData['valeur-intrinseque-indice-sanitaire'] = health_column.toString();
  }

  if (aesthetic_column !== undefined && aesthetic_column !== null) {
    formData['valeur-intrinseque-indice-esthetique'] = aesthetic_column.toString();
  }

  if (location_index !== undefined && location_index !== null) {
    formData['valeur-intrinseque-indice-situation'] = location_index.toString();
  }

  if (variety_grade !== undefined && variety_grade !== null) {
    formData['valeur-intrinseque-indice-espece'] = variety_grade.toString();
  }
  if (!formData['valeur-intrinseque-indice-espece'] && arbre.espece.indice_espece) {
    formData['valeur-intrinseque-indice-espece'] = arbre.espece.indice_espece.toString();
  }

  // Espece information - note: no indiceEspece in the new structure


  // ========================================
  // Additional fields that might be useful
  // ========================================

  // Store the original espece data for reference
  if (arbre.espece) {
    formData['_espece'] = arbre.espece;
  }

  // Store location data
  if (arbre.coord) {
    formData['_coord'] = arbre.coord;
  }
  if (arbre.address) {
    formData['_address'] = arbre.address;
  }
  if (arbre.ville) {
    formData['_ville'] = arbre.ville;
  }
  if (arbre.pays) {
    formData['_pays'] = arbre.pays;
  }
  if (arbre.beva) {
    formData['beva'] = arbre.beva.toString();
  }

  if (arbre.imgUrl) {
    formData['img1'] = arbre.imgUrl.img1;
    formData['img2'] = arbre.imgUrl.img2;
    formData['img3'] = arbre.imgUrl.img3;
  }

  // Store metadata
  formData['_inventaireId'] = serverData.data.id;
  formData['_arbreId'] = arbre.id;
  formData['_isFinished'] = serverData.data.is_finished;
  formData['_userId'] = serverData.data.user_id;
  formData['_createdAt'] = serverData.data.created_at;
  formData['_updatedAt'] = serverData.data.updated_at;
  formData['_userEditedDateTravaux'] = serverData.data.arbre.user_edited_date_travaux;
  formData['_userEditedDateProVisite'] = serverData.data.arbre.user_edited_date_pro_visite;

  // Store full user data for print header
  if (serverData.data.user) {
    formData['_user'] = serverData.data.user;
  }

  return formData;
}
export function mapServerDataEpaysageToFormDataIntervention(serverData: InventaireResponse): FormData {
  if (!serverData.data) {
    throw new Error('Server data is required');
  }
  const { epaysage } = serverData.data;
  if (!epaysage) {
    return {};
  }
  const { essence } = epaysage;
  if (!essence || essence.length === 0) {
    return {};
  }
  const arbre = essence[0];
  if (!arbre) {
    return {};
  }
  const formData: FormData = {};

  // ========================================
  // STEP 1: Caractéristiques du sujet
  // ========================================

  // Diameter and height
  if (arbre.diametre !== undefined) {
    formData['diametre'] = arbre.diametre;
  }
  if (arbre.hauteur !== undefined) {
    formData['hauteur'] = arbre.hauteur;
  }
  if (epaysage.num_sujet) {
    formData['num_sujet'] = epaysage.num_sujet;
  }
  if (epaysage?.code_site) {
    formData['code_site'] = epaysage.code_site;
  }

  // Au pied de l'arbre - maps to caract_pied (multiselect)
  // if (arbre.caract_pied) {
  //   formData['au-pied-de-l-arbre'] = arbre.caract_pied;
  // }
  // if (arbre.caract_pied_other) {
  //   formData['au-pied-de-l-arbre_other'] = arbre.caract_pied_other;
  // }

  // // Tronc characteristics - maps to caract_tronc
  // if (arbre.caract_tronc) {
  //   formData['caracteristiques-du-sujet-tronc'] = arbre.caract_tronc;
  // }
  // if (arbre.caract_tronc_multiples) {
  //   formData['caracteristiques-du-sujet-tronc_tronc-multiples'] = arbre.caract_tronc_multiples;
  // }

  // Port de l'arbre - maps to port_arbre
  if (arbre.port_arbre) {
    formData['port-de-larbre'] = arbre.port_arbre;
  }

  // Stade de développement - maps to stade_dev
  if (arbre.stade_dev) {
    formData['stade-de-developpement'] = arbre.stade_dev;
  }

  // ========================================
  // STEP 2: Aspect remarquable
  // ========================================

  // Critères remarquables - maps to critere
  if (arbre.critere) {
    formData['critere-remarquable'] = arbre.critere;
  }
  if (arbre.critere_other) {
    formData['critere-remarquable_autre-precisez'] = arbre.critere_other;
  }

  // ========================================
  // STEP 3: État sanitaire
  // ========================================

  // État sanitaire système racinaire et collet - maps to etat_san_collet
  // if (arbre.etat_san_collet) {
  //   formData['etat-sanitaire-systme-racinaire-collet'] = arbre.etat_san_collet;
  // }
  // if (arbre.etat_san_collet_cavite) {
  //   formData['etat-sanitaire-systme-racinaire-collet_cavit-nombre'] = arbre.etat_san_collet_cavite;
  // }
  // if (arbre.etat_san_collet_champignons) {
  //   formData['etat-sanitaire-systme-racinaire-collet_champignons-lignivores'] = arbre.etat_san_collet_champignons;
  // }
  // if (arbre.etat_san_collet_champignons_autres) {
  //   formData['etat-sanitaire-systme-racinaire-collet_champignons-lignivores_autres'] = arbre.etat_san_collet_champignons_autres;
  // }
  // if (arbre.etat_san_collet_other) {
  //   formData['etat-sanitaire-systme-racinaire-collet_autre-precisez'] = arbre.etat_san_collet_other;
  // }

  // // État sanitaire tronc - maps to etat_san_tronc
  // if (arbre.etat_san_tronc) {
  //   formData['etat-sanitaire-tronc'] = arbre.etat_san_tronc;
  // }
  // if (arbre.etat_san_tronc_cavite) {
  //   formData['etat-sanitaire-tronc_cavit-nombre'] = arbre.etat_san_tronc_cavite;
  // }
  // if (arbre.etat_san_tronc_corps_etranger) {
  //   formData['etat_san_tronc_corps_etranger'] = arbre.etat_san_tronc_corps_etranger;
  // }
  // if (arbre.etat_san_tronc_champignons) {
  //   formData['etat-sanitaire-tronc_champignons-lignivores'] = arbre.etat_san_tronc_champignons;
  // }
  // if (arbre.etat_san_tronc_champignons_autres) {
  //   formData['etat-sanitaire-tronc_champignons-lignivores_autres'] = arbre.etat_san_tronc_champignons_autres;
  // }
  // if (arbre.etat_san_tronc_nuisibles) {
  //   formData['etat-sanitaire-tronc_parasite'] = arbre.etat_san_tronc_nuisibles;
  // }
  // if (arbre.etat_san_tronc_nuisibles_autres) {
  //   formData['etat-sanitaire-tronc_parasite-other'] = arbre.etat_san_tronc_nuisibles_autres;
  // }
  // if (arbre.etat_san_tronc_other) {
  //   formData['etat-sanitaire-tronc_other'] = arbre.etat_san_tronc_other;
  // }

  // // État sanitaire houppier - maps to etat_san_houppier
  // if (arbre.etat_san_houppier) {
  //   formData['etat-sanitaire-houppier'] = arbre.etat_san_houppier;
  // }
  // if (arbre.etat_san_houppier_champignons) {
  //   formData['etat-sanitaire-houppier_champignons-lignivores'] = arbre.etat_san_houppier_champignons;
  // }
  // if (arbre.etat_san_houppier_champignons_autres) {
  //   formData['etat-sanitaire-houppier_champignons-lignivores_autres'] = arbre.etat_san_houppier_champignons_autres;
  // }
  // if (arbre.etat_san_houppier_nuisibles) {
  //   formData['etat-sanitaire-houppier_parasite'] = arbre.etat_san_houppier_nuisibles;
  // }
  // if (arbre.etat_san_houppier_nuisibles_autres) {
  //   formData['etat-sanitaire-houppier_parasite-other'] = arbre.etat_san_houppier_nuisibles_autres;
  // }
  // if (arbre.etat_san_houppier_other) {
  //   formData['etat-sanitaire-houppier_other'] = arbre.etat_san_houppier_other;
  // }

  // // État sanitaire général - maps to etat_san_general
  // if (arbre.etat_san_general && arbre.etat_san_general.length > 0) {
  //   formData['etat-sanitaire-general'] = arbre.etat_san_general; // Radio field, take first value
  // }
  // if (arbre.etat_san_general_other) {
  //   formData['etat-sanitaire-general_other'] = arbre.etat_san_general_other;
  // }

  // if (arbre.proximite_with_dict && arbre.proximite_with_dict.length > 0) {
  //   formData['dict'] = arbre.proximite_with_dict;
  // }

  // ========================================
  // STEP 4: Risque de rupture
  // ========================================

  // Transform risque array to table format expected by the form
  // if (arbre.risque && Array.isArray(arbre.risque)) {
  //   const risqueTable: { [key: string]: string } = {};
  //   arbre.risque.forEach(risk => {
  //     Object.entries(risk).forEach(([part, level]) => {
  //       //@ts-ignore
  //       risqueTable[part] = level;
  //     });
  //   });
  //   formData['risque-de-rupture'] = risqueTable;
  // }

  // Risque général - maps to risque_general
  // if (arbre.risque_general && arbre.risque_general.length > 0) {
  //   formData['risque-de-rupture-generale'] = arbre.risque_general[0]; // Radio field
  // }
  // if (arbre.risque_general_other) {
  //   formData['risque-de-rupture-generale_other'] = arbre.risque_general_other;
  // }

  // ========================================
  // STEP 5: Environnement
  // ========================================

  // Implantation - maps to implantation
  // if (arbre.implantation) {
  //   formData['implantation'] = arbre.implantation;
  // }

  // // Domaine - maps to domaine
  // if (arbre.domaine) {
  //   formData['domaine'] = arbre.domaine;
  // }

  // // Nuisances constatées - maps to nuisance
  // if (arbre.nuisance) {
  //   formData['nuisances-constatees'] = arbre.nuisance;
  // }

  // // Proximité directe - maps to proximite
  // if (arbre.proximite) {
  //   formData['proximite-directe'] = arbre.proximite;
  // }
  // if (arbre.proximite_other) {
  //   formData['proximite-directe_other'] = arbre.proximite_other;
  // }

  // // DICT - maps to dict
  // if (arbre.dict) {
  //   formData['dict'] = arbre.dict;
  // }

  // // Taux de fréquentation - maps to taux_freq
  // if (arbre.taux_freq) {
  //   formData['taux-de-frquentation'] = arbre.taux_freq;
  // }

  // // Type de passage - maps to type_passage
  // if (arbre.type_passage) {
  //   formData['type-de-passage'] = arbre.type_passage;
  // }
  // if (arbre.type_passage_other) {
  //   formData['type-de-passage_other'] = arbre.type_passage_other;
  // }

  // // Accessibilité - maps to accessibilite
  // if (arbre.accessibilite) {
  //   formData['accessibilite'] = arbre.accessibilite;
  // }
  // if (arbre.accessibilite_other) {
  //   formData['accessibilite_other'] = arbre.accessibilite_other;
  // }

  // ========================================
  // STEP 6: Préconisation de travaux
  // ========================================

  // Date travaux - maps to date_travaux
  if (arbre.date_travaux) {
    formData['preco-travaux-date-travaux'] = arbre.date_travaux;
  }
  if (arbre.date_pro_visite) {
    formData['preco-travaux-prochaine-visite'] = arbre.date_pro_visite;
  }


  // Work recommendations - map various travaux fields
  if (arbre.abattage) {
    formData['preco-travaux-abattage'] = arbre.abattage;
  }


  // Multiple work types
  // if (arbre.travaux_collet_multiple) {
  //   formData['preco-travaux-collet'] = arbre.travaux_collet_multiple;
  // }

  // if (arbre.travaux_tronc_multiple) {
  //   formData['preco-travaux-tronc'] = arbre.travaux_tronc_multiple;
  // }

  // if (arbre.travaux_houppier_multiple) {
  //   formData['preco-travaux-houppier'] = arbre.travaux_houppier_multiple;
  // }


  // // Work comments and other fields
  // if (arbre.travaux_collet_other) {
  //   formData['preco-travaux-collet_other'] = arbre.travaux_collet_other;
  // }
  // if (arbre.travaux_tronc_other) {
  //   formData['preco-travaux-tronc_other'] = arbre.travaux_tronc_other;
  // }
  // if (arbre.travaux_houppier_other) {
  //   formData['preco-travaux-houppier_other'] = arbre.travaux_houppier_other;
  // }
  // if (arbre.travaux_tronc_protection) {
  //   formData['preco-travaux-tronc_protection'] = arbre.travaux_tronc_protection;
  // }
  // // Work comments
  // if (arbre.travaux_commentaire) {
  //   formData['preco-travaux-com'] = arbre.travaux_commentaire;
  // }

  // ========================================
  // STEP 7: Estimation de la valeur (BEVA)
  // ========================================

  // Get BEVA data from serverData.data (not arbre)
  const { health_index, aesthetic_index, location_index, variety_grade, health_column, aesthetic_column } = serverData.data;

  // Map BEVA fields directly to form data with new field names
  if (variety_grade !== undefined && variety_grade !== null) {
    formData['variety_grade'] = variety_grade;
  }

  if (health_index !== undefined && health_index !== null) {
    formData['health_index'] = health_index;
  }

  if (aesthetic_index !== undefined && aesthetic_index !== null) {
    formData['aesthetic_index'] = aesthetic_index;
  }

  if (location_index !== undefined && location_index !== null) {
    formData['location_index'] = location_index;
  }

  if (health_column !== undefined && health_column !== null) {
    formData['health_column'] = health_column;
  }

  if (aesthetic_column !== undefined && aesthetic_column !== null) {
    formData['aesthetic_column'] = aesthetic_column;
  }

  // Keep backward compatibility - map to old field names if needed for legacy components
  if (health_column !== undefined && health_column !== null) {
    formData['valeur-intrinseque-indice-sanitaire'] = health_column.toString();
  }

  if (aesthetic_column !== undefined && aesthetic_column !== null) {
    formData['valeur-intrinseque-indice-esthetique'] = aesthetic_column.toString();
  }

  if (location_index !== undefined && location_index !== null) {
    formData['valeur-intrinseque-indice-situation'] = location_index.toString();
  }

  if (variety_grade !== undefined && variety_grade !== null) {
    formData['valeur-intrinseque-indice-espece'] = variety_grade.toString();
  }
  // if (!formData['valeur-intrinseque-indice-espece'] && arbre.espece.indice_espece) {
  //   formData['valeur-intrinseque-indice-espece'] = arbre.espece.indice_espece.toString();
  // }

  // Espece information - note: no indiceEspece in the new structure


  // ========================================
  // Additional fields that might be useful
  // ========================================

  // Store the original espece data for reference
  if (arbre.espece) {
    formData['_espece'] = arbre.espece;
  }

  // Store location data
  // if (arbre.coord) {
  //   formData['_coord'] = arbre.coord;
  // }
  // if (arbre.address) {
  //   formData['_address'] = arbre.address;
  // }
  // if (arbre.ville) {
  //   formData['_ville'] = arbre.ville;
  // }
  // if (arbre.pays) {
  //   formData['_pays'] = arbre.pays;
  // }
  // if (arbre.beva) {
  //   formData['beva'] = arbre.beva.toString();
  // }

  // if (arbre.imgUrl) {
  //   formData['img1'] = arbre.imgUrl.img1;
  //   formData['img2'] = arbre.imgUrl.img2;
  //   formData['img3'] = arbre.imgUrl.img3;
  // }

  // Store metadata
  formData['_inventaireId'] = serverData.data.id;
  formData['_arbreId'] = arbre.id;
  formData['_isFinished'] = serverData.data.is_finished;
  formData['_userId'] = serverData.data.user_id;
  formData['_createdAt'] = serverData.data.created_at;
  formData['_updatedAt'] = serverData.data.updated_at;
  // formData['_userEditedDateTravaux'] = serverData.data.arbre.user_edited_date_travaux;
  // formData['_userEditedDateProVisite'] = serverData.data.arbre.user_edited_date_pro_visite;

  // Store full user data for print header
  if (serverData.data.user) {
    formData['_user'] = serverData.data.user;
  }

  return formData;
}
/**
 * Helper function to map health index number to descriptive string
 */
function mapHealthIndexToDescription(healthIndex: number): string {
  const healthItem = HEALTH_INDEX_TAB.find((item: DataIndexConst) => item.name === healthIndex);
  return healthItem?.displayName || 'Sain - Vigoureux';
}

/**
 * Helper function to map aesthetic index number to descriptive string
 */
function mapAestheticIndexToDescription(aestheticIndex: number): string {
  const aestheticItem = AESTHETIC_INDEX_TAB.find((item: DataIndexConst) => item.name === aestheticIndex);
  return aestheticItem?.displayName || 'Solitaire - Beau sujet';
}

/**
 * Helper function to map location index number to descriptive string
 */
function mapLocationIndexToDescription(locationIndex: number): string {
  const locationItem = LOCATION_INDEX_TAB.find((item: DataIndexConst) => item.name === locationIndex);
  return locationItem?.displayName || 'Centre ville';
}

/**
 * Helper function to map health display name back to index number
 */
function mapHealthDescriptionToIndex(displayName: string): number {
  const healthItem = HEALTH_INDEX_TAB.find((item: DataIndexConst) => item.displayName === displayName);
  return healthItem?.name || 1;
}

/**
 * Helper function to map aesthetic display name back to index number
 */
function mapAestheticDescriptionToIndex(displayName: string): number {
  const aestheticItem = AESTHETIC_INDEX_TAB.find((item: DataIndexConst) => item.displayName === displayName);
  return aestheticItem?.name || 2;
}

/**
 * Helper function to map location display name back to index number
 */
function mapLocationDescriptionToIndex(displayName: string): number {
  const locationItem = LOCATION_INDEX_TAB.find((item: DataIndexConst) => item.displayName === displayName);
  return locationItem?.name || 10;
}

/**
 * Reverse mapper: transforms form data back to server format for saving
 */
export function mapFormDataToServerData(formData: FormData, originalData?: InventaireServerResponse): Partial<InventaireServerResponse> {
  if (!originalData) {
    throw new Error('Original data is required for mapping form data back to server format');
  }

  const arbreData: any = {};

  // Map back the form fields to server structure (snake_case)
  if (formData['diametre'] !== undefined) arbreData.diametre = formData['diametre'];
  if (formData['hauteur'] !== undefined) arbreData.hauteur = formData['hauteur'];
  if (formData['num_sujet'] !== undefined) arbreData.num_sujet = formData['num_sujet'];
  if (formData['code_site'] !== undefined) arbreData.code_site = formData['code_site'];
  if (formData['age_estime'] !== undefined) arbreData.age_estime = Number(formData['age_estime']);
  arbreData.caract_pied = formData['au-pied-de-l-arbre'] || [];
  arbreData.caract_pied_other = formData['au-pied-de-l-arbre_other'] || '';
  arbreData.caract_tronc = formData['caracteristiques-du-sujet-tronc'] || '';
  arbreData.caract_tronc_multiples = formData['caracteristiques-du-sujet-tronc_tronc-multiples'] || null;
  arbreData.port_arbre = formData['port-de-larbre'] || '';
  arbreData.stade_dev = formData['stade-de-developpement'] || '';

  // Critères remarquables
  arbreData.critere = formData['critere-remarquable'] || [];
  arbreData.critere_other = formData['critere-remarquable_autre-precisez'] || '';

  // État sanitaire
  arbreData.etat_san_collet = formData['etat-sanitaire-systme-racinaire-collet'] || [];
  arbreData.etat_san_collet_cavite = Number(formData['etat-sanitaire-systme-racinaire-collet_cavit-nombre']) || null;
  arbreData.etat_san_collet_champignons = formData['etat-sanitaire-systme-racinaire-collet_champignons-lignivores']?.map((item: string) => Number(item)) || [];
  arbreData.etat_san_collet_champignons_autres = formData['etat-sanitaire-systme-racinaire-collet_champignons-lignivores_autres'] || '';
  arbreData.etat_san_collet_other = formData['etat-sanitaire-systme-racinaire-collet_autre-precisez'] || '';

  arbreData.etat_san_tronc = formData['etat-sanitaire-tronc'] || [];
  arbreData.etat_san_tronc_cavite = Number(formData['etat-sanitaire-tronc_cavit-nombre']) || null;
  arbreData.etat_san_tronc_corps_etranger = formData['etat_san_tronc_corps_etranger'] || '';
  arbreData.etat_san_tronc_champignons = formData['etat-sanitaire-tronc_champignons-lignivores']?.map((item: string) => Number(item)) || [];
  arbreData.etat_san_tronc_champignons_autres = formData['etat-sanitaire-tronc_champignons-lignivores_autres'] || '';
  arbreData.etat_san_tronc_nuisibles = formData['etat-sanitaire-tronc_parasite'] || [];
  arbreData.etat_san_tronc_nuisibles_autres = formData['etat-sanitaire-tronc_parasite-other'] || '';
  arbreData.etat_san_tronc_other = formData['etat-sanitaire-tronc_other'] || '';

  arbreData.etat_san_houppier = formData['etat-sanitaire-houppier'] || [];
  arbreData.etat_san_houppier_champignons = formData['etat-sanitaire-houppier_champignons-lignivores']?.map((item: string) => Number(item)) || [];
  arbreData.etat_san_houppier_champignons_autres = formData['etat-sanitaire-houppier_champignons-lignivores_autres'] || '';
  arbreData.etat_san_houppier_nuisibles = formData['etat-sanitaire-houppier_parasite'] || [];
  arbreData.etat_san_houppier_nuisibles_autres = formData['etat-sanitaire-houppier_parasite-other'] || '';
  arbreData.etat_san_houppier_other = formData['etat-sanitaire-houppier_other'] || '';

  arbreData.etat_san_general = formData['etat-sanitaire-general'] || [];
  arbreData.etat_san_general_other = formData['etat-sanitaire-general_other'] || '';

  // Risques
  if (formData['risque-de-rupture']) {
    const risqueTable = formData['risque-de-rupture'] as { [key: string]: string };
    arbreData.risque = Object.entries(risqueTable).map(([part, level]) => ({ [part]: level }));
  }
  arbreData.risque_general = formData['risque-de-rupture-generale'] || [];
  arbreData.risque_general_other = formData['risque-de-rupture-generale_other'] || '';

  // Environnement
  arbreData.implantation = formData['implantation'] || '';
  arbreData.domaine = formData['domaine'] || '';
  arbreData.nuisance = formData['nuisances-constatees'] || [];
  arbreData.proximite = formData['proximite-directe'] || [];
  arbreData.proximite_other = formData['proximite-directe_other'] || '';
  arbreData.proximite_with_dict = formData['dict'] || [];
  arbreData.taux_freq = formData['taux-de-frquentation'] || '';
  arbreData.type_passage = formData['type-de-passage'] || [];
  arbreData.type_passage_other = formData['type-de-passage_other'] || '';
  arbreData.accessibilite = formData['accessibilite'] || '';
  arbreData.accessibilite_other = formData['accessibilite_other'] || '';

  // Travaux
  arbreData.date_travaux = formData['preco-travaux-date-travaux'] || '';
  arbreData.date_pro_visite = formData['preco-travaux-prochaine-visite'] || '';
  arbreData.travaux_commentaire = formData['preco-travaux-com'] || '';
  arbreData.abattage = formData['preco-travaux-abattage'] || null;
  arbreData.travaux_collet_multiple = formData['preco-travaux-collet'] || [];
  arbreData.travaux_collet_other = formData['preco-travaux-collet_other'] || '';
  arbreData.travaux_tronc_multiple = formData['preco-travaux-tronc'] || [];
  arbreData.travaux_tronc_other = formData['preco-travaux-tronc_other'] || '';
  arbreData.travaux_tronc_protection = formData['preco-travaux-tronc_protection'] || '';
  arbreData.travaux_houppier_multiple = formData['preco-travaux-houppier'] || [];
  arbreData.travaux_houppier_other = formData['preco-travaux-houppier_other'] || '';
  arbreData.proximite_with_dict = formData['dict'] || [];
  // Map BEVA data back to top-level inventaire data
  const topLevelData: any = { ...originalData.data };

  // Use new field names directly if they exist
  if (formData['valeur-intrinseque-indice-espece'] !== undefined) {
    topLevelData.variety_grade = Number(formData['valeur-intrinseque-indice-espece']);
  }
  if (formData['health_index'] !== undefined) {
    topLevelData.health_index = Number(formData['health_index']);
  }
  if (formData['aesthetic_index'] !== undefined) {
    topLevelData.aesthetic_index = Number(formData['aesthetic_index']);
  }
  if (formData['location_index'] !== undefined) {
    topLevelData.location_index = Number(formData['location_index']);
  }
  if (formData['health_column'] !== undefined) {
    topLevelData.health_column = Number(formData['health_column']);
  }
  if (formData['aesthetic_column'] !== undefined) {
    topLevelData.aesthetic_column = Number(formData['aesthetic_column']);
  }

  // Fallback to old field names for backward compatibility
  if (formData['valeur-intrinseque-indice-sanitaire'] && !formData['health_column']) {
    topLevelData.health_index = mapHealthDescriptionToIndex(formData['valeur-intrinseque-indice-sanitaire'] as string);
    topLevelData.health_column = Number(formData['valeur-intrinseque-indice-sanitaire']);
  }
  if (formData['valeur-intrinseque-indice-esthetique'] && !formData['aesthetic_column']) {
    topLevelData.aesthetic_index = mapAestheticDescriptionToIndex(formData['valeur-intrinseque-indice-esthetique'] as string);
    topLevelData.aesthetic_column = Number(formData['valeur-intrinseque-indice-esthetique']);
  }
  if (formData['valeur-intrinseque-indice-situation'] && !formData['location_index']) {
    topLevelData.location_index = mapLocationDescriptionToIndex(formData['valeur-intrinseque-indice-situation'] as string);
  }
  if (formData['valeur-intrinseque-indice-espece'] && !formData['valeur-intrinseque-indice-espece']) {
    topLevelData.variety_grade = Number(formData['valeur-intrinseque-indice-espece']);
  }

  if (formData['_espece']) {
    arbreData.espece_id = Number(formData['_espece'].id);
  }
  if (formData['_userEditedDateTravaux']) {
    arbreData.user_edited_date_travaux = new Date(formData['_userEditedDateTravaux'])
  }
  if (formData['_userEditedDateProVisite']) {
    arbreData.user_edited_date_pro_visite = new Date(formData['_userEditedDateProVisite']);
  }


  return {
    data: {
      ...topLevelData,
      arbre: {
        ...arbreData
      }
    }
  };
}
